import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import PaymentMethodSelector from '../components/PaymentMethodSelector';

const Checkout = () => {
  const { cart, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [selectedReceiptFile, setSelectedReceiptFile] = useState(null);

  const [formData, setFormData] = useState({
    nombreUsuario: '',
    fullName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    paymentMethod: ''
  });

  useEffect(() => {
    loadPaymentConfig();
  }, []);

  const loadPaymentConfig = async () => {
    try {
      setLoadingConfig(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/payment-config`);
      const data = await response.json();

      if (data.success) {
        setPaymentConfig(data.data);
        const availableMethods = [];
        if (data.data.efectivo?.enabled) availableMethods.push('efectivo');
        if (data.data.transferencia?.enabled) availableMethods.push('transferencia');

        if (availableMethods.length > 0) {
          setFormData(prev => ({ ...prev, paymentMethod: availableMethods[0] }));
        }
      } else {
        setError('Error al cargar métodos de pago');
      }
    } catch (error) {
      console.error('Error loading payment config:', error);
      setError('Error al cargar métodos de pago');
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePaymentMethodChange = (method) => {
    setFormData({ ...formData, paymentMethod: method });
    setError('');
  };

  const validateForm = () => {
    if (!formData.nombreUsuario.trim()) {
      setError('El nombre de usuario es obligatorio');
      return false;
    }

    const requiredFields = ['fullName', 'address', 'city', 'state', 'zipCode', 'phone', 'paymentMethod'];
    for (const field of requiredFields) {
      if (!formData[field]) {
        setError(`El campo ${field} es requerido`);
        return false;
      }
    }

    if (!paymentConfig) {
      setError('Configuración de pagos no disponible');
      return false;
    }

    if (formData.paymentMethod === 'transferencia') {
      if (!paymentConfig.transferencia?.enabled) {
        setError('Transferencia bancaria no está disponible');
        return false;
      }
      if (!selectedReceiptFile) {
        setError('Debe seleccionar un comprobante de transferencia');
        return false;
      }
    } else if (formData.paymentMethod === 'efectivo') {
      if (!paymentConfig.efectivo?.enabled) {
        setError('Pago en efectivo no está disponible');
        return false;
      }
    } else {
      setError('Método de pago no válido');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Preparar items del carrito local para enviar al backend
      const itemsToSend = cart.items.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        size: item.size || null,
        color: item.color || null
      }));

      const response = await fetch(`${process.env.REACT_APP_API_URL}/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nombreUsuario: formData.nombreUsuario.trim(),
          items: itemsToSend,
          shippingAddress: {
            fullName: formData.fullName,
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zipCode: formData.zipCode,
            phone: formData.phone
          },
          paymentMethod: formData.paymentMethod
        })
      });

      const data = await response.json();

      if (data.success) {
        // Si es transferencia y hay comprobante, subirlo
        if (formData.paymentMethod === 'transferencia' && selectedReceiptFile) {
          try {
            const receiptFormData = new FormData();
            receiptFormData.append('receipt', selectedReceiptFile);

            await fetch(`${process.env.REACT_APP_API_URL}/receipts/${data.order.id}`, {
              method: 'POST',
              body: receiptFormData
            });
          } catch (uploadErr) {
            console.error('Error subiendo comprobante:', uploadErr);
          }
        }

        // Limpiar carrito local
        clearCart();
        alert('¡Pedido creado exitosamente!');
        navigate('/');
      } else {
        setError(data.message || 'Error al crear el pedido');
      }
    } catch (err) {
      setError('Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  if (!cart || !cart.items || cart.items.length === 0) {
    return (
      <div className="container" style={{ padding: '4rem 1rem', textAlign: 'center' }}>
        <h2>No hay productos en el carrito</h2>
      </div>
    );
  }

  const total = getCartTotal();

  if (loadingConfig) {
    return (
      <div className="container" style={{ padding: '4rem 1rem', textAlign: 'center' }}>
        <h2>Cargando métodos de pago...</h2>
      </div>
    );
  }

  const shouldShowReceiptUpload =
    formData.paymentMethod === 'transferencia' &&
    paymentConfig?.transferencia?.enabled;

  const isFormValid = () => {
    const hasNombreUsuario = formData.nombreUsuario.trim().length > 0;

    const hasValidPaymentMethod = formData.paymentMethod && (
      (formData.paymentMethod === 'efectivo' && paymentConfig?.efectivo?.enabled) ||
      (formData.paymentMethod === 'transferencia' && paymentConfig?.transferencia?.enabled)
    );

    const hasRequiredReceipt = formData.paymentMethod !== 'transferencia' || selectedReceiptFile;

    return hasNombreUsuario && hasValidPaymentMethod && hasRequiredReceipt;
  };

  return (
    <div className="container" style={{ padding: '2rem 1rem', minHeight: '70vh' }}>
      <h1 style={{ marginBottom: '2rem' }}>Checkout</h1>

      {error && <div style={{ padding: '1rem', background: '#fee2e2', color: '#991b1b', borderRadius: '8px', marginBottom: '1rem' }}>{error}</div>}

      <form onSubmit={handleSubmit} style={{ maxWidth: '600px' }}>
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#374151' }}>Datos del comprador</h3>
          <input
            type="text"
            name="nombreUsuario"
            placeholder="Nombre de usuario *"
            value={formData.nombreUsuario}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd', marginBottom: '1rem', fontSize: '16px' }}
          />
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#374151' }}>Información de Envío</h3>
          <div style={{ display: 'grid', gap: '1rem' }}>
            <input
              type="text"
              name="fullName"
              placeholder="Nombre completo"
              value={formData.fullName}
              onChange={handleChange}
              required
              style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '16px' }}
            />
            <input
              type="text"
              name="address"
              placeholder="Dirección"
              value={formData.address}
              onChange={handleChange}
              required
              style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '16px' }}
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <input
                type="text"
                name="city"
                placeholder="Ciudad"
                value={formData.city}
                onChange={handleChange}
                required
                style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '16px' }}
              />
              <input
                type="text"
                name="state"
                placeholder="Estado/Provincia"
                value={formData.state}
                onChange={handleChange}
                required
                style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '16px' }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <input
                type="text"
                name="zipCode"
                placeholder="Código Postal"
                value={formData.zipCode}
                onChange={handleChange}
                required
                style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '16px' }}
              />
              <input
                type="tel"
                name="phone"
                placeholder="Teléfono"
                value={formData.phone}
                onChange={handleChange}
                required
                style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '16px' }}
              />
            </div>
          </div>
        </div>

        <PaymentMethodSelector
          selectedMethod={formData.paymentMethod}
          onMethodChange={handlePaymentMethodChange}
          paymentConfig={paymentConfig}
          error={error && error.includes('método') ? error : ''}
        />

        {shouldShowReceiptUpload && (
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '1rem', color: '#374151' }}>
              Comprobante de Transferencia
            </h3>
            <div style={{
              border: '2px dashed #ddd',
              borderRadius: '8px',
              padding: '2rem',
              textAlign: 'center',
              backgroundColor: selectedReceiptFile ? '#f0f9ff' : '#fafafa'
            }}>
              {selectedReceiptFile ? (
                <div>
                  <div style={{ marginBottom: '1rem' }}>
                    <p style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold' }}>Archivo seleccionado</p>
                    <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>{selectedReceiptFile.name}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedReceiptFile(null)}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Cambiar archivo
                  </button>
                </div>
              ) : (
                <div>
                  <p style={{ margin: '0 0 1rem 0', fontWeight: 'bold' }}>
                    Selecciona tu comprobante
                  </p>
                  <p style={{ margin: '0 0 1rem 0', fontSize: '14px', color: '#666' }}>
                    Screenshot del comprobante bancario
                  </p>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setSelectedReceiptFile(file);
                        setError('');
                      }
                    }}
                    style={{
                      padding: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      width: '100%',
                      maxWidth: '300px',
                      fontSize: '16px'
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{ background: '#f9fafb', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.25rem', fontWeight: 'bold' }}>
            <span>Total:</span><span>${total.toFixed(2)}</span>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || !isFormValid()}
          style={{
            width: '100%',
            padding: '1rem',
            background: loading || !isFormValid()
              ? '#9ca3af'
              : '#03355A',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: loading || !isFormValid() ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Procesando...' : 'Confirmar Pedido'}
        </button>
      </form>
    </div>
  );
};

export default Checkout;
